import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { pageIndicator } from "../apollo";
import {
  ComponentTabLink,
  ITabLink,
  ITabTitle,
  PageType,
} from "../components/component-tab";
import { AsideAdminComponent } from "../layout/aside-admin";
import { useListLinkServiceSelect } from "../hooks/useListLinkServiceSelect";
import {
  CrStaffUserInput,
  CrStaffUserMutation,
  useCrStaffUser,
} from "../hooks/useCrStaffUser";
import { UserRole, WorkerType } from "../generated/graphql";

export interface ICrStaffUserFormProps {
  username: string;
  password: string;
  name?: string;
  phone?: string;
}

export const AdminCrAccount = () => {
  const navigate = useNavigate();
  const tabList: ITabLink[] = [];
  const stitle: ITabTitle = { text: "계정 등록" };

  const [role, setRole] = useState<UserRole>(UserRole.Admin);
  const [workerType, setWorkerType] = useState<WorkerType>(WorkerType.Driver);
  const [linkServiceIds, setLinkServiceIds] = useState<number[]>([]);

  const [
    listLinkServiceSelect,
    { data: linkserviceData },
  ] = useListLinkServiceSelect();

  useEffect(() => {
    pageIndicator("account");
    listLinkServiceSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<ICrStaffUserFormProps>({ mode: "all" });

  const onComplete = async (data: CrStaffUserMutation) => {
    const {
      crStaffUser: { ok, err },
    } = data;
    if (ok) {
      alert("계정이 생성되었습니다.");
      navigate("/account");
    } else {
      alert(err);
    }
  };
  const [crStaffUser, { loading }] = useCrStaffUser(onComplete);

  const toggleService = (id: number) => {
    setLinkServiceIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const onSubmit = () => {
    if (loading) return;
    const { username, password, name, phone } = getValues();
    const input: CrStaffUserInput = {
      username,
      password,
      ...(name && { name }),
      ...(phone && { phone }),
      role,
      ...(role === UserRole.Worker && { workerType }),
      linkServiceIds,
    };
    crStaffUser({ variables: { input } });
  };

  return (
    <div className="bg-main bg-opacity-50 w-full h-full min-h-screen">
      <div className="w-full mx-auto h-full min-h-screen bg-gray-100">
        <main id="main">
          <div className="grid grid-cols-[200px_1fr] max-w-full">
            <AsideAdminComponent />
            <div className="mypages bg-gray-100 w-full min-w-[1200px] overflow-hidden">
              <div className="orderWrap">
                <div className="h-fix-content relative">
                  <div className={`w-full h-full px-10 py-8`}>
                    <ComponentTabLink
                      tabs={tabList}
                      stitle={stitle}
                      type={PageType.Create}
                    />
                    <div className="shadow-lg w-full bg-white">
                      <form onSubmit={handleSubmit(onSubmit)} id="create">
                        <div className="pl-9 pr-11 py-8 space-y-4 max-w-[640px]">
                          <div className="space-y-2">
                            <p className="font-bold">아이디</p>
                            <input
                              {...register("username", { required: true })}
                              type="text"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                            {errors.username && (
                              <p className="text-[#B42713] text-xs">
                                아이디를 입력해주세요.
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">비밀번호</p>
                            <input
                              {...register("password", { required: true })}
                              type="password"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                            {errors.password && (
                              <p className="text-[#B42713] text-xs">
                                비밀번호를 입력해주세요.
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">이름</p>
                            <input
                              {...register("name")}
                              type="text"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">연락처</p>
                            <input
                              {...register("phone")}
                              type="text"
                              className="rounded-lg border w-full py-2 px-3"
                            />
                          </div>
                          <div className="space-y-2">
                            <p className="font-bold">권한</p>
                            <div className="flex flex-wrap gap-x-6">
                              <label className="flex flex-wrap items-center gap-x-2">
                                <input
                                  type="radio"
                                  name="role"
                                  checked={role === UserRole.Admin}
                                  onChange={() => setRole(UserRole.Admin)}
                                />
                                관리자
                              </label>
                              <label className="flex flex-wrap items-center gap-x-2">
                                <input
                                  type="radio"
                                  name="role"
                                  checked={role === UserRole.Worker}
                                  onChange={() => setRole(UserRole.Worker)}
                                />
                                직원
                              </label>
                            </div>
                          </div>
                          {role === UserRole.Worker && (
                            <div className="space-y-2">
                              <p className="font-bold">직군</p>
                              <select
                                className="rounded-lg border py-2 px-3"
                                value={workerType}
                                onChange={(e) =>
                                  setWorkerType(e.target.value as WorkerType)
                                }
                              >
                                <option value={WorkerType.Driver}>기사</option>
                                <option value={WorkerType.Packer}>패커</option>
                                <option value={WorkerType.Pilot}>조종사</option>
                              </select>
                            </div>
                          )}
                          <div className="space-y-2">
                            <p className="font-bold">배정 서비스</p>
                            <p className="text-xs text-[#8B8C8C]">
                              선택한 서비스의 거래처/상품/배너/배송관리/주문관리만
                              볼 수 있습니다. 하나도 선택하지 않으면 전체
                              서비스를 볼 수 있는 수퍼관리자로 취급됩니다.
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-2">
                              {linkserviceData?.listLinkServiceSelect.linkservice?.map(
                                (service) => (
                                  <label
                                    key={`service-${service.id}`}
                                    className="flex flex-wrap items-center gap-x-2"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={linkServiceIds.includes(
                                        service.id
                                      )}
                                      onChange={() =>
                                        toggleService(service.id)
                                      }
                                    />
                                    {service.name}
                                  </label>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
